import KC from '@kalvi-education/kalvium-sdk/src/keycloak2'
import dayjs from 'dayjs'
import { get } from 'lodash'

import { ALERT_TYPES } from '../enums/alert'

const ALERT_IMAGES = {
    PMF_EMBED: 'PMF_EMBED',
    PMF_UPLOAD: 'PMF_UPLOAD',
    PMF_FALLBACK: 'PMF_FALLBACK',
    CR_APPROVED: 'CHANGE_REQUEST_APPROVED',
    CR_REJECTED: 'CHANGE_REQUEST_REJECTED',
}

const _getUserDetails = () => {
    const userEmail = KC.getParsedToken().email ?? 'Unknown User'
    const userSquads = KC.getParsedToken().groups ?? ['Unknown Squad']

    const squadNumbers = userSquads.map(squad => {
        const match = squad.match(/Squad (\d+)/)
        return match ? match[1] : squad
    })

    const squadList =
        squadNumbers.length > 1 ? squadNumbers.join(', ') : squadNumbers[0]

    return {
        email: userEmail,
        squads: squadList,
    }
}

const userAgent = navigator?.userAgent
const userAgentData = navigator?.userAgentData
const browserName =
    userAgentData?.brands?.at(-2)?.brand ?? userAgent ?? 'Unknown'
const platform = userAgentData?.platform ?? userAgent ?? 'Unknown'

const _sanitizeAxiosError = err => {
    if (!err) return
    const error = JSON.parse(err)
    if (!error || typeof error !== 'object') return error

    const sanitizedError = { ...error }

    if (sanitizedError?.config?.headers) {
        const headers = { ...sanitizedError.config.headers }
        if (headers.Authorization) {
            headers.Authorization = 'Bearer [REDACTED]'
        }
        sanitizedError.config.headers = headers
    }

    return JSON.stringify(sanitizedError)
}

const getPmfAlertData = (data, { subtitle }, alertImage) => {
    const { email, squads } = _getUserDetails()
    const title = get(data, 'title') || '🙈 Stackblitz Error!'
    const duration = data?.metadata?.duration
    const questionType = data?.metadata?.url?.includes('assessment')
        ? 'Assessment'
        : 'Assignment'

    const errorInfo = `
    🤔 <b>Error: ${_sanitizeAxiosError(data?.metadata?.error) || 'Unknown'}</b>
    💬 <b>Error Message: ${data?.metadata?.errorMessage || 'Unavailable'}</b>
    ⏱️ <b>Duration: ${duration ? duration + 's' : 'NA'}</b>`

    return {
        title,
        subtitle,
        description: `
    📱 <b>User: ${email} </b>
    🚀 <b>Squads: ${squads}</b>
    🌀 <b>Question Type: ${questionType}</b>
    ❓ <b>Question Template: ${
            data?.metadata?.questionTemplateType || 'NA'
            }</b>${alertImage !== ALERT_IMAGES.PMF_FALLBACK ? errorInfo : ''}
    🌐 <b>Browser: ${browserName}</b>
    🕸️ <b>OS: ${platform}</b>
    📅 <b>Timestamp: ${dayjs().format('DD/MM/YYYY HH:mm:ss')}</b>
    🧭 <b>Domain: <a href=${data?.metadata?.url}/>${data?.metadata?.url}<a/></b>
    `.trim(),
        module: 'PMF',
        alert_image: alertImage,
    }
}

const getSchedulerAlertData = (data, { subtitle }, alertImage) => {
    const { email, squads: userSquads } = _getUserDetails();
    const metadata = data?.metadata || {};
    
    const initialValues = metadata.initialValues || {};
    const requestBody = metadata.requestBody || {};
    const squads = metadata.squads || userSquads || 'NA';
    const slotTime = metadata.initialValues.time || 'NA';

    const formatData = obj =>
        Object.entries(obj)
            .map(([key, value]) => `🔹 <b>${key}:</b> ${value || 'NA'}`)
            .join('<br>');

    return {
        title: '🗓️Scheduler Pulse',
        subtitle: subtitle,
        description: `
        📱 <b>User:</b> ${email}  
        🚀 <b>Squads:</b> ${squads}
        🕒 <b>Slot Timing: </b> ${slotTime}

        <br><b>🔄 OLD SCHEDULER DATA:</b>  
        ${formatData(initialValues)}

        <br><b>📩 NEW SCHEDULER DATA/ERROR:</b>
        ${typeof requestBody === 'string' ? requestBody : formatData(requestBody)}
        `,
        module: 'SCHEDULER',
        alert_image: alertImage,
    }
}

const getAlertData = data => {
    switch (data.type) {
        case ALERT_TYPES.PMF_EMBED_FAILED:
            return getPmfAlertData(
                data,
                { subtitle: '❌ Failed to embed stackblitz.' },
                ALERT_IMAGES.PMF_EMBED
            )
        case ALERT_TYPES.PMF_UPLOAD_FAILED:
            return getPmfAlertData(
                data,
                { subtitle: '⏫ GCP Upload Failed.' },
                ALERT_IMAGES.PMF_UPLOAD
            )
        case ALERT_TYPES.SWITCHED_TO_FALLBACK_MODE:
            data.title = '🥷🏼 Fallback Mode Activated!'
            return getPmfAlertData(
                data,
                {
                    subtitle:
                        '🪷 The system has switched to a stable fallback state to ensure continuity.',
                },
                ALERT_IMAGES.PMF_FALLBACK
            )
        case ALERT_TYPES.CHANGE_REQUEST_APPROVED:
            return getSchedulerAlertData(
                data,
                { subtitle: '✅ Change Request Approved.' },
                ALERT_IMAGES.CR_APPROVED
            )
        case ALERT_TYPES.CHANGE_REQUEST_REJECTED:
            return getSchedulerAlertData(
                data,
                { subtitle: '❌ Change Request Rejected.' },
                ALERT_IMAGES.CR_REJECTED
            )
        default:
            return null
    }
}
export { getAlertData }
